import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useExtraCost, useApproveExtraCosts } from '../../hooks/useEmployeeExtraCosts';
import { getExtraCostDownloadUrl } from '../../services/api/employeeExtraCosts';
import { Spinner } from '../../components/common/Spinner';
import { Button } from '../../components/common/Button';
import { DismissibleErrorBanner } from '../../components/common/DismissibleErrorBanner';
import { formatCurrencyStrict as formatCurrency, formatDisplayDate } from '../../utils/format';
import { formatMonthYear } from '../../utils/dateFormat';

function formatDate(dateStr: string): string {
  return formatDisplayDate(dateStr); // MKS-351
}

function monthYearLabel(month: number, year: number): string {
  // MKS-351 — "October 2026" from the shared util.
  return formatMonthYear(new Date(year, month - 1));
}

export default function EmployeeExtraCostDetailPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const id = idParam ? parseInt(idParam, 10) : undefined;

  const { data: cost, isLoading, error } = useExtraCost(id);
  const approveMutation = useApproveExtraCosts();
  // F2-005: surface approve-mutation rejections inline. Previously an
  // unawaited rejection (network blip / 409) emitted only an
  // unhandledrejection + Sentry tag with no user-visible feedback.
  const [actionError, setActionError] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  if (error || !cost) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error ? (error as Error).message : 'Extra cost not found'}</p>
        <button onClick={() => navigate('/admin/employee-extra-costs')} className="mt-2 text-sm text-blue-600 hover:text-blue-800">
          &larr; Back to list
        </button>
      </div>
    );
  }

  const userName = cost.user
    ? [cost.user.firstName, cost.user.lastName].filter(Boolean).join(' ') || cost.user.email
    : '-';

  const statusStyles: Record<string, string> = {
    new: 'bg-blue-50 text-blue-700 border-blue-300',
    pending: 'bg-gray-100 text-gray-700 border-gray-300',
    partial: 'bg-yellow-50 text-yellow-700 border-yellow-300',
    applied: 'bg-green-50 text-green-700 border-green-300',
  };

  const handleApprove = async () => {
    if (!id) return;
    setActionError(null);
    try {
      await approveMutation.mutateAsync([id]);
    } catch (err) {
      setActionError((err as Error).message || 'Failed to approve extra cost');
    }
  };

  const progressPercent = cost.amount > 0 ? Math.min(100, (cost.appliedAmount / cost.amount) * 100) : 0;

  const handleDownload = async () => {
    if (!id) return;
    const url = await getExtraCostDownloadUrl(id);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/admin/employee-extra-costs')}
          className="text-sm text-gray-500 hover:text-gray-700 mb-1"
        >
          &larr; Back to Employee Extra Costs
        </button>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-gray-900">{cost.description}</h1>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusStyles[cost.status] || statusStyles.pending}`}>
              {cost.status}
            </span>
            {cost.recurring && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                Recurring ({cost.frequencyMonths}mo)
              </span>
            )}
          </div>
          {cost.state === 'new' && (
            <Button size="sm" onClick={handleApprove} isLoading={approveMutation.isPending}>
              Approve
            </Button>
          )}
        </div>
      </div>

      <DismissibleErrorBanner message={actionError} onDismiss={() => setActionError(null)} />

      {/* Detail Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
          <div>
            <div className="text-sm text-gray-500">Employee</div>
            <div className="text-sm font-medium text-gray-900 mt-1">{userName}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Amount</div>
            <div className="text-sm font-medium text-gray-900 mt-1">{formatCurrency(cost.amount)}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Cost Date</div>
            <div className="text-sm font-medium text-gray-900 mt-1">{formatDate(cost.costDate)}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">File</div>
            <div className="mt-1">
              {cost.hasFile ? (
                <Button variant="ghost" size="sm" onClick={handleDownload}>
                  {cost.fileName || 'Download'}
                </Button>
              ) : (
                <span className="text-sm text-gray-400">No file</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Deduction Tracker */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Deduction Tracker</h2>

        {/* Progress Bar */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-500">
              Original: <span className="font-medium text-gray-900">{formatCurrency(cost.amount)}</span>
            </span>
            <span className="text-gray-500">
              Applied: <span className="font-medium text-gray-900">{formatCurrency(cost.appliedAmount)}</span>
            </span>
            <span className="text-gray-500">
              Remaining: <span className="font-medium text-gray-900">{formatCurrency(cost.remainingAmount)}</span>
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-green-500 h-2.5 rounded-full transition-all"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {/* Applications Table */}
        {cost.applications && cost.applications.length > 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Month/Year
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount Deducted
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {cost.applications.map((app) => (
                  <tr key={app.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {monthYearLabel(app.month, app.year)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">
                      {formatCurrency(app.appliedAmount)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        app.invoiceState === 'sent'
                          ? 'bg-blue-50 text-blue-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {app.invoiceState || 'open'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      <Link
                        to={`/reports/freelancer-invoices/${cost.userId}/${app.year}/${app.month}`}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-medium">
                  <td className="px-4 py-3 text-sm text-gray-900">Total Applied</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">
                    {formatCurrency(cost.appliedAmount)}
                  </td>
                  <td />
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded-lg border-2 border-dashed border-gray-200 p-8 text-center">
            <p className="text-gray-500">No deductions yet — this cost has not been applied to any invoice.</p>
          </div>
        )}
      </div>
    </div>
  );
}
